import React from 'react';
import ServerError from '../../../../components/ServerError';
import { useQueryParams } from '../../../../hooks/useQueryParams';
import {
  OrganizationLoginOptionType,
  useGetAuthenticationOptionsQuery,
} from '../../__generated__/graphql';
import { useTranslation } from 'react-i18next';
import Loading from './Loading';
import LoginWithApiToken from './LoginWithApiToken';
import LoginWithSaml from './LoginWithSaml';
import LoginPasswordFormJwt from './LoginPasswordFormJwt';
import LoginWithAuthorizationCode from './LoginWithAuthorizationCode';

const LoginPage: React.FC = () => {
  const { data, loading, error } = useGetAuthenticationOptionsQuery();
  const { t } = useTranslation();

  if (loading) {
    return <Loading />;
  }

  if (error || !data) {
    return <ServerError />;
  }

  if ((data.getAuthenticationOptions?.options ?? []).length > 1) {
    const hasPassword = data.getAuthenticationOptions?.options?.some(
      (option) => option.type === OrganizationLoginOptionType.Password,
    );

    return (
      <>
        {hasPassword && (
          <div className="mb-3">
            <LoginPasswordFormJwt />
            <p className="text-muted text-center">{t('signIn.or')}</p>
          </div>
        )}
        {!hasPassword && (
          <>
            <h1 className="display-4 text-center mb-3">{t('signIn.title')}</h1>
            <p className="text-muted text-center mb-5">
              {t('signInSaml.multipleProviders')}
            </p>
          </>
        )}
        {data.getAuthenticationOptions?.options?.map((option) => {
          if (option.type === OrganizationLoginOptionType.Saml) {
            return (
              <LoginWithSaml
                key={option.id}
                providerId={option.id}
                providerName={option.name}
                headless
                className="mb-3"
              />
            );
          }
          return null;
        })}
      </>
    );
  }

  if (data.getAuthenticationOptions?.saml) {
    return (
      <LoginWithSaml
        providerId={data.getAuthenticationOptions.saml.id}
        providerName={data.getAuthenticationOptions.saml.name}
      />
    );
  }

  return <LoginPasswordFormJwt />;
};

const Login: React.FC = () => {
  const { apiToken, authorizationCode } = useQueryParams();

  if (authorizationCode) {
    return <LoginWithAuthorizationCode authorizationCode={authorizationCode} />;
  }

  if (apiToken) {
    return <LoginWithApiToken apiToken={apiToken} />;
  }

  return <LoginPage />;
};

export default Login;
